<template>
  <div class="unload-daily-page nx-page nx-report-page">
    <nx-report-header
      title="铁路卸车作业效率日报"
      subtitle="基于到货计划、实际到站、班组排班与翻车机作业记录统计进度、时长与延误"
    />

    <el-card shadow="never" class="filter-card">
      <el-form inline :model="query">
        <el-form-item label="作业时间范围">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd"
            unlink-panels
            @change="onTimeRangeChange"
          />
        </el-form-item>
        <el-form-item label="班组">
          <el-select v-model="query.teamNo" clearable filterable placeholder="全部班组" style="min-width: 160px">
            <el-option v-for="t in teamOptions" :key="t.teamNo" :label="t.teamName || t.teamNo" :value="t.teamNo" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备">
          <el-select
            v-model="query.id"
            clearable
            filterable
            remote
            :remote-method="remoteDeviceSearch"
            :loading="deviceLoading"
            placeholder="全部设备"
            style="min-width: 160px"
            @change="onDeviceChange"
          >
            <el-option v-for="d in deviceOptions" :key="d.id" :label="d.deviceName || d.id" :value="d.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="延误状态">
          <el-radio-group v-model="query.delayStatus">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button label="normal">正常</el-radio-button>
            <el-radio-button label="delayed">延误</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="applyFilter">查询</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="16" class="kpi-row">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ kpi.arrivedCount != null ? kpi.arrivedCount : 0 }} 车</div>
          <div class="kpi-label">区间卸车数</div>
          <el-progress :percentage="num(kpi.completionRatePercent, 0)" :format="() => '完成率 ' + fmtNum(kpi.completionRatePercent, 1) + '%'" />
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ fmtNum(kpi.avgUnloadHoursPerCar, 1) }} 小时/车</div>
          <div class="kpi-label">平均卸车时长</div>
          <div class="kpi-sub" :class="deltaClass(kpi.avgUnloadHoursDeltaVsYesterday)">
            较昨日 {{ fmtDeltaHour(kpi.avgUnloadHoursDeltaVsYesterday) }}
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val warn">{{ kpi.delayedTrainCount != null ? kpi.delayedTrainCount : 0 }} 车</div>
          <div class="kpi-label">延误车次</div>
          <div class="kpi-sub">延误率 {{ fmtNum(kpi.delayRatePercent, 1) }}%</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ fmtNum(kpi.equipmentUtilizationPercent, 1) }}%</div>
          <div class="kpi-label">设备利用率</div>
          <div class="kpi-sub">最佳利用率 {{ fmtNum(kpi.optimalUtilizationBenchmark, 0) }}%</div>
          <el-progress :percentage="num(kpi.equipmentUtilizationPercent, 0)" :color="utilColor" />
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card">
      <div class="card-head">
        <span class="card-title">卸车时长趋势</span>
        <el-radio-group v-model="trendType" size="small" @change="loadTrend">
          <el-radio-button label="hour">按小时</el-radio-button>
          <el-radio-button label="shift">按班组</el-radio-button>
          <el-radio-button label="device">按设备</el-radio-button>
        </el-radio-group>
      </div>
      <div ref="trendChart" class="echart-box" />
    </el-card>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">延误原因分析</span>
          </div>
          <div ref="delayBarChart" class="echart-box bar-h" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">班组效率排名</span>
          </div>
          <div class="rank-list">
            <div v-for="(row, idx) in shiftRankList" :key="row.shiftName + idx" class="rank-row">
              <span class="rank-no">{{ idx + 1 }}</span>
              <span class="rank-name">{{ row.shiftName }}</span>
              <el-progress
                :percentage="rankBarPct(row, shiftRankList)"
                :show-text="false"
                class="rank-bar"
              />
              <span class="rank-val">{{ fmtNum(row.avgHoursPerCar, 1) }}h/车</span>
            </div>
            <div v-if="!shiftRankList.length" class="empty-tip">暂无数据</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card">
      <div class="card-head">
        <span class="card-title">设备效率对比</span>
      </div>
      <el-row :gutter="12">
        <el-col v-for="d in deviceCompareList" :key="d.deviceCode" :xs="24" :sm="8">
          <div class="dev-card">
            <div class="dev-name">{{ d.deviceName }}</div>
            <div class="dev-meta">{{ fmtNum(d.avgHoursPerCar, 1) }} 小时/车</div>
            <div class="dev-eff">效率 {{ fmtNum(d.efficiencyPercent, 0) }}%</div>
          </div>
        </el-col>
      </el-row>
      <div v-if="!deviceCompareList.length" class="empty-tip">暂无设备维度数据</div>
    </el-card>

    <el-card shadow="hover" class="block-card">
      <div class="card-head">
        <span class="card-title">作业明细数据</span>
        <div>
          <el-input v-model="tableKeyword" clearable maxlength="30" placeholder="车次" style="width: 160px; margin-right: 8px" @keyup.enter.native="reloadTable" />
          <el-button type="primary" size="small" @click="reloadTable">筛选</el-button>
          <el-button size="small" icon="el-icon-download" @click="exportExcel">导出</el-button>
        </div>
      </div>
      <el-table v-loading="tableLoading" :data="tableRecords" border stripe style="width: 100%">
        <el-table-column prop="trainNumber" label="到站车次" min-width="120">
          <template slot-scope="{ row }">
            <span class="link">{{ row.trainNumber || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="operationDate" label="作业日期" width="120" />
        <el-table-column prop="planUnloadTime" label="计划卸车时间" width="165" :formatter="fmtDt" />
        <el-table-column prop="actualUnloadTime" label="实际卸车时间" width="165" :formatter="fmtDt" />
        <el-table-column prop="unloadDurationHours" label="卸车时长(h/车)" width="130" align="right" :formatter="fmtHour" />
        <el-table-column prop="delayMinutes" label="延误时长" width="110" align="right">
          <template slot-scope="{ row }">
            <span :class="delayClass(row.delayMinutes)">{{ fmtDelay(row.delayMinutes) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="unloadDeviceName" label="卸车设备" width="120" show-overflow-tooltip />
        <el-table-column prop="shiftTeamName" label="作业班组" width="100" />
        <el-table-column prop="delayReason" label="延误原因" min-width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="row.delayReason" class="delay-reason-cell">{{ row.delayReason }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="tableTotal"
        :page-size="pagination.size"
        :current-page.sync="pagination.page"
        @current-change="loadTable"
      />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import {
  fetchKpi,
  fetchDurationTrend,
  fetchDelayReasonBars,
  fetchShiftRanking,
  fetchDeviceCompare,
  fetchDetailPage,
  fetchTeamOptions,
  fetchDevicePycode
} from './api'

export default {
  name: 'RailwayUnloadEfficiencyDaily',
  components: { NxReportHeader },
  data () {
    const today = new Date()
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      dateRange: [fmt(today), fmt(today)],
      query: {
        teamNo: '',
        id: '',
        deviceName: '',
        faultLocation: '',
        delayStatus: ''
      },
      tableKeyword: '',
      trendType: 'hour',
      kpi: {},
      teamOptions: [],
      deviceOptions: [],
      deviceLoading: false,
      shiftRankList: [],
      deviceCompareList: [],
      tableRecords: [],
      tableTotal: 0,
      tableLoading: false,
      pagination: { page: 1, size: 10 },
      trendChart: null,
      delayBarChart: null,
      colors: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#9b59b6', '#00bcd4']
    }
  },
  computed: {
    utilColor () {
      const t = Number(this.kpi.optimalUtilizationBenchmark) || 85
      const v = Number(this.kpi.equipmentUtilizationPercent) || 0
      if (v >= t) return '#67c23a'
      if (v >= t * 0.85) return '#e6a23c'
      return '#f56c6c'
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.trendChart = echarts.init(this.$refs.trendChart)
      this.delayBarChart = echarts.init(this.$refs.delayBarChart)
      window.addEventListener('resize', this.onResize)
      this.remoteDeviceSearch('')
      this.applyFilter()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    this.trendChart && this.trendChart.dispose()
    this.delayBarChart && this.delayBarChart.dispose()
  },
  methods: {
    basePayload () {
      const start = this.dateRange && this.dateRange[0]
      const end = this.dateRange && this.dateRange[1]
      return {
        startTime: start ? `${start} 00:00:00` : undefined,
        endTime: end ? `${end} 23:59:59` : undefined,
        teamNo: this.query.teamNo || undefined,
        // 设备筛选字段后端 DTO 叫 deviceCode，但其值实际就是 base_device_manage.id
        deviceCode: this.query.id ? String(this.query.id) : undefined,
        delayStatus: this.query.delayStatus || undefined
      }
    },
    async onTimeRangeChange () {
      await this.loadTeamOptions()
    },
    async loadTeamOptions () {
      try {
        const { data } = await fetchTeamOptions()
        const page = (data && data.data) || {}
        const raw = page.records || []
        this.teamOptions = raw.map((r) => ({
          teamNo: r.teamNo,
          teamName: r.teamName || r.teamNo
        }))
      } catch (e) {
        this.teamOptions = []
      }
    },
    async remoteDeviceSearch (q) {
      const key = q || ''
      this.deviceLoading = true
      try {
        const { data } = await fetchDevicePycode({ deviceName: key })
        const raw = Array.isArray(data) ? data : []
        this.deviceOptions = raw.map((r) => ({
          id: r.id,
          deviceName: r.deviceName,
          deviceLocation: r.deviceLocation
        }))
      } catch (e) {
        this.deviceOptions = []
      } finally {
        this.deviceLoading = false
      }
    },
    onDeviceChange (value) {
      const item = this.deviceOptions.find((d) => d.id === value)
      this.query.deviceName = item ? item.deviceName : ''
      this.query.faultLocation = item ? item.deviceLocation : ''
    },
    async applyFilter () {
      if (!this.dateRange || this.dateRange.length !== 2 || !this.dateRange[0] || !this.dateRange[1]) {
        this.$message.warning('请选择作业时间范围')
        return
      }
      const start = new Date(this.dateRange[0])
      const end = new Date(this.dateRange[1])
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        this.$message.warning('作业时间范围格式不合法')
        return
      }
      if (start.getTime() > end.getTime()) {
        this.$message.warning('开始时间不能大于结束时间')
        return
      }
      this.pagination.page = 1
      await this.loadTeamOptions()
      await Promise.all([
        this.loadKpi(),
        this.loadTrend(),
        this.loadDelayBars(),
        this.loadShiftRank(),
        this.loadDeviceCmp(),
        this.loadTable()
      ])
    },
    async loadKpi () {
      try {
        const { data } = await fetchKpi(this.basePayload())
        this.kpi = data.data || {}
      } catch (e) {
        this.kpi = {}
      }
    },
    async loadTrend () {
      if (!this.trendChart) return
      try {
        const { data } = await fetchDurationTrend({ ...this.basePayload(), trendType: this.trendType })
        const d = data.data || {}
        const hours = d.hours || []
        const series = d.series || []
        const ser = series.map((s, i) => ({
          name: s.name,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          data: (s.data || []).map((v) => (v != null ? Number(v) : null)),
          itemStyle: { color: this.colors[i % this.colors.length] }
        }))
        this.trendChart.setOption(
          {
            tooltip: { trigger: 'axis' },
            legend: { type: 'scroll', bottom: 0 },
            grid: { left: '3%', right: '4%', bottom: ser.length > 1 ? '18%' : '10%', top: '12%', containLabel: true },
            xAxis: { type: 'category', data: hours, boundaryGap: false },
            yAxis: { type: 'value', name: '小时', min: 0 },
            series: ser
          },
          true
        )
      } catch (e) {
        this.$message.error('趋势图加载失败')
      }
    },
    async loadDelayBars () {
      if (!this.delayBarChart) return
      try {
        const { data } = await fetchDelayReasonBars(this.basePayload())
        const d = data.data || {}
        const cats = d.categories || []
        const vals = (d.values || []).map((v) => Number(v) || 0)
        this.delayBarChart.setOption(
          {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
            xAxis: { type: 'value' },
            yAxis: { type: 'category', data: cats, axisLabel: { interval: 0 } },
            series: [
              {
                type: 'bar',
                data: vals,
                itemStyle: { color: '#409EFF' },
                barMaxWidth: 28,
                label: { show: true, position: 'right' }
              }
            ]
          },
          true
        )
      } catch (e) {
        this.$message.error('延误原因图加载失败')
      }
    },
    async loadShiftRank () {
      try {
        const { data } = await fetchShiftRanking(this.basePayload())
        const wrap = data.data || {}
        this.shiftRankList = wrap.list || []
      } catch (e) {
        this.shiftRankList = []
      }
    },
    async loadDeviceCmp () {
      try {
        const { data } = await fetchDeviceCompare(this.basePayload())
        const wrap = data.data || {}
        this.deviceCompareList = wrap.list || []
      } catch (e) {
        this.deviceCompareList = []
      }
    },
    reloadTable () {
      this.pagination.page = 1
      this.loadTable()
    },
    async loadTable () {
      this.tableLoading = true
      try {
        const { data } = await fetchDetailPage({
          ...this.basePayload(),
          keyword: this.tableKeyword || undefined,
          page: this.pagination.page,
          size: this.pagination.size
        })
        const page = data.data || {}
        this.tableRecords = page.records || []
        this.tableTotal = page.total || 0
      } catch (e) {
        this.tableRecords = []
        this.tableTotal = 0
      } finally {
        this.tableLoading = false
      }
    },
    onResize () {
      this.trendChart && this.trendChart.resize()
      this.delayBarChart && this.delayBarChart.resize()
    },
    num (v, fb) {
      const n = Number(v)
      if (isNaN(n)) return fb
      return Math.min(100, Math.max(0, n))
    },
    fmtNum (v, d) {
      if (v === null || v === undefined || v === '') return '-'
      return Number(v).toFixed(d)
    },
    fmtDt (row, col, val) {
      if (!val) return '-'
      const s = String(val)
      return s.length > 16 ? s.slice(0, 16) : s
    },
    fmtHour (row, col, val) {
      if (val === null || val === undefined || val === '') return '-'
      return Number(val).toFixed(2)
    },
    fmtDelay (m) {
      if (m === null || m === undefined) return '-'
      const n = Number(m)
      if (n === 0) return '0分钟'
      if (n > 0) return `${n}分钟`
      return `${n}分钟`
    },
    delayClass (m) {
      if (m == null) return ''
      return Number(m) > 0 ? 'delay-pos' : Number(m) < 0 ? 'delay-neg' : ''
    },
    fmtDeltaHour (v) {
      if (v === null || v === undefined || v === '') return '-'
      const n = Number(v)
      if (n === 0) return '持平'
      const abs = Math.abs(n).toFixed(1)
      return n > 0 ? `+${abs}小时` : `-${abs}小时`
    },
    deltaClass (v) {
      if (v == null) return ''
      return Number(v) > 0 ? 'bad' : Number(v) < 0 ? 'good' : ''
    },
    rankBarPct (row, list) {
      if (!list || !list.length) return 0
      const max = Math.max(...list.map((r) => Number(r.avgHoursPerCar) || 0), 0.001)
      const v = Number(row.avgHoursPerCar) || 0
      // 时长越短效率越高，条形越长
      return Math.round(Math.max(0, Math.min(100, ((max - v) / max) * 100)))
    },
    async exportExcel () {
      const maxRows = 2000
      const size = Math.min(this.tableTotal || maxRows, maxRows)
      if (size < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      try {
        const { data } = await fetchDetailPage({
          ...this.basePayload(),
          keyword: this.tableKeyword || undefined,
          page: 1,
          size
        })
        const list = (data.data && data.data.records) || []
        const { export_json_to_excel } = require('@/util/vendor/Export2Excel')
        const th = [
          '到站车次',
          '作业日期',
          '计划卸车时间',
          '实际卸车时间',
          '卸车时长',
          '延误时长(分)',
          '卸车设备',
          '作业班组',
          '延误原因'
        ]
        const rows = list.map((r) => [
          r.trainNumber || '',
          r.operationDate || '',
          this.fmtDt(null, null, r.planUnloadTime),
          this.fmtDt(null, null, r.actualUnloadTime),
          r.unloadDurationHours != null ? Number(r.unloadDurationHours).toFixed(2) : '',
          r.delayMinutes != null ? String(r.delayMinutes) : '',
          r.unloadDeviceName || '',
          r.shiftTeamName || '',
          r.delayReason || ''
        ])
        export_json_to_excel(th, rows, '卸车作业效率明细')
        if (this.tableTotal > maxRows) {
          this.$message.warning(`导出前 ${maxRows} 条`)
        }
      } catch (e) {
        this.$message.error('导出失败')
      }
    }
  }
}
</script>

<style scoped>
.unload-daily-page {
  padding: 0;
  background: transparent;
  min-height: auto;
}
.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}
.page-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: #909399;
}
.filter-card {
  margin-bottom: 16px;
}
.kpi-row {
  margin-bottom: 16px;
}
.kpi-card {
  padding: 12px;
  min-height: 110px;
}
.kpi-val {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
}
.kpi-val.warn {
  color: #f56c6c;
}
.kpi-label {
  font-size: 13px;
  color: #606266;
  margin: 6px 0;
}
.kpi-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
.kpi-sub.good {
  color: #67c23a;
}
.kpi-sub.bad {
  color: #f56c6c;
}
.block-card {
  margin-bottom: 16px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
}
.echart-box {
  width: 100%;
  height: 340px;
}
.echart-box.bar-h {
  height: 280px;
}
.rank-list {
  padding: 4px 0;
}
.rank-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.rank-no {
  width: 22px;
  color: #909399;
  font-weight: 600;
}
.rank-name {
  width: 72px;
  font-size: 13px;
}
.rank-bar {
  flex: 1;
}
.rank-val {
  width: 72px;
  text-align: right;
  font-size: 13px;
  color: #606266;
}
.dev-card {
  background: #f9fafc;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid #ebeef5;
}
.dev-name {
  font-weight: 600;
  margin-bottom: 6px;
}
.dev-meta,
.dev-eff {
  font-size: 13px;
  color: #606266;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 16px;
}
.link {
  color: #409eff;
  cursor: pointer;
}
.delay-pos {
  color: #f56c6c;
  font-weight: 600;
}
.delay-neg {
  color: #67c23a;
}
.delay-reason-cell {
  background: #fdf6ec;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
</style>
